import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { format } from "date-fns";
import { formatToTwoDecimal } from "../../../../utils/numberFormatter";

// Shared by the PDF and Excel exports of the View Account transaction history.
// Two layouts:
// - Per day: like the Petty Cash Summary sheet, one tab/page per day that has
//   transactions (days without any are skipped):
//     CASH BALANCE (opening) -> one row per transaction -> TOTAL -> BALANCE
// - Summary: the whole range in one table (no tabs) with Cash In / Cash Out
//   and a running Balance carried over from before the range
// REF is "<day no.>#<row no.>", e.g. 1#1, 1#2 on the first day, 2#1 on the next

// Debit = money in (Deposit), Credit = money out (Withdraw)
export const signedAmount = (row) =>
  row.type === "Debit" ? Number(row.amount || 0) : -Number(row.amount || 0);

// Same "To" / "From" logic as the Transaction History table
const payeeName = (row) =>
  row.type === "Credit"
    ? row.sub3_tos?.account_name || row.transacted || "---"
    : row.transacted || row.sub3_tos?.account_name || "---";

// Balances are computed here in date order instead of using the stored
// account_balance, which follows entry order and breaks on backdated entries
export const buildAccountSummary = (
  transactions = [],
  dayNo = 1,
  openingBalance = 0,
) => {
  // The table is newest first; the summary reads oldest first
  const ordered = [...transactions].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
  );

  // expense_* fields are only set for Local / Overseas Expenses, Payable,
  // Bulk Collection, Other Income and Transfer rows (see processTransaction
  // in backend account.helper.js)
  const rows = ordered.map((row, index) => ({
    ref: `${dayNo}#${index + 1}`,
    payee: row.expense_payee || payeeName(row),
    accountTitle:
      row.expense_account_title ||
      row.module_from ||
      row.payment_method ||
      "---",
    particulars:
      row.expense_particulars ||
      row.check_or_remarks ||
      row.transaction_number ||
      "---",
    type: row.type === "Debit" ? "Deposit" : "Withdraw",
    amount: Number(row.amount || 0),
  }));

  const totalWithdraw = rows
    .filter((row) => row.type === "Withdraw")
    .reduce((sum, row) => sum + row.amount, 0);
  const totalDeposit = rows
    .filter((row) => row.type === "Deposit")
    .reduce((sum, row) => sum + row.amount, 0);

  return {
    rows,
    openingBalance,
    totalWithdraw,
    totalDeposit,
    balance: openingBalance + totalDeposit - totalWithdraw,
  };
};

// One summary per transaction date, oldest day first, numbered #1, #2, ...
// openingBalance is the balance before the first day; each day then opens
// with the previous day's closing balance
export const buildDailySummaries = (transactions = [], openingBalance = 0) => {
  const byDay = new Map();
  transactions.forEach((row) => {
    if (!row.date) return;
    const key = format(new Date(row.date), "yyyy-MM-dd");
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(row);
  });

  let runningBalance = openingBalance;
  return [...byDay.keys()].sort().map((key, index) => {
    const summary = buildAccountSummary(
      byDay.get(key),
      index + 1,
      runningBalance,
    );
    runningBalance = summary.balance;

    const dayDate = new Date(`${key}T00:00:00`);
    return {
      dayNo: index + 1,
      label: `#${index + 1}`,
      date: format(dayDate, "EEEE, MMMM dd, yyyy"),
      shortDate: format(dayDate, "dd-MMM-yy"),
      ...summary,
    };
  });
};

// Whole range as one table: every day's rows in order with Cash In / Cash
// Out and a running Balance. Built from the daily summaries so REF and
// balances match the Per day layout
export const buildRangeSummary = (days = [], openingBalance = 0, fromDate) => {
  let runningBalance = openingBalance;
  const rows = days.flatMap((day) =>
    day.rows.map((row) => {
      const cashIn = row.type === "Deposit" ? row.amount : 0;
      const cashOut = row.type === "Withdraw" ? row.amount : 0;
      runningBalance += cashIn - cashOut;
      return {
        ...row,
        date: day.shortDate,
        cashIn,
        cashOut,
        balance: runningBalance,
      };
    }),
  );

  return {
    rows,
    openingBalance,
    totalCashIn: rows.reduce((sum, row) => sum + row.cashIn, 0),
    totalCashOut: rows.reduce((sum, row) => sum + row.cashOut, 0),
    balance: runningBalance,
  };
};

// Balance before `fromDate`: sum of every transaction dated earlier
// (same Debit + / Credit - sum as /accountListSub/getBalance)
export const balanceBefore = (allTransactions = [], fromDate) => {
  const startKey = format(new Date(fromDate), "yyyy-MM-dd");
  return allTransactions
    .filter(
      (row) => row.date && format(new Date(row.date), "yyyy-MM-dd") < startKey,
    )
    .reduce((sum, row) => sum + signedAmount(row), 0);
};

// Plain amount; deposits (green) and withdrawals (red) differ by color only
export const displayAmount = (row) => formatToTwoDecimal(row.amount);

// Report letterhead from Company Profile. profile_picture is raw base64;
// PNGs start with "iVBOR", anything else is treated as JPEG
export const buildCompanyInfo = (company) => {
  const picture = company?.profile_picture || null;
  const logoExt = picture?.startsWith("iVBOR") ? "png" : "jpeg";

  return {
    name: (company?.name || "").toUpperCase(),
    // Centered under the name; address, contact and email share one line
    // so the name gets the room
    lines: [
      [
        company?.address && `Address: ${company.address}`,
        company?.phone && `Contact Number: ${company.phone}`,
        company?.email && `Email Address: ${company.email}`,
      ]
        .filter(Boolean)
        .join("  |  "),
      company?.sub_name,
    ].filter(Boolean),
    logo: picture ? `data:image/${logoExt};base64,${picture}` : null,
    logoBase64: picture,
    logoExt,
  };
};

// Signature block under each day's summary
export const SIGNATORIES = ["Prepared By:", "Checked By:", "Approved By:"];

export const SUMMARY_COLUMNS = [
  "REF.",
  "PAYEE NAME",
  "ACCOUNT TITLE",
  "PARTICULARS",
  "AMOUNT",
];

export const RANGE_COLUMNS = [
  "DATE",
  "REF.",
  "PAYEE NAME",
  "ACCOUNT TITLE",
  "PARTICULARS",
  "CASH IN",
  "CASH OUT",
  "BALANCE",
];

const AMOUNT_FORMAT = "#,##0.00";
const thinBorder = {
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
};

// Label in the Particulars column, value in the Amount column
const addSummaryRow = (sheet, label, value, fill) => {
  const row = sheet.addRow(["", "", "", label, value]);
  row.font = { bold: true };
  row.getCell(4).alignment = { horizontal: "right" };
  row.getCell(5).numFmt = AMOUNT_FORMAT;
  if (fill) {
    row.getCell(5).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: fill },
    };
  }
  [4, 5].forEach((col) => (row.getCell(col).border = thinBorder));
};

const addWorkbookLogo = (workbook, company) =>
  company.logoBase64
    ? workbook.addImage({
        base64: company.logoBase64,
        extension: company.logoExt,
      })
    : null;

// Letterhead (logo top-left, name + details centered, at least 5 rows so the
// logo has room), the right-aligned date line, then the black title band,
// each merged across `colCount` columns
const addSheetHeader = (
  sheet,
  { company, logoId, dateText, titleText, colCount },
) => {
  const nameRow = sheet.addRow([company.name]);
  sheet.mergeCells(nameRow.number, 1, nameRow.number, colCount);
  nameRow.getCell(1).alignment = { horizontal: "center" };
  nameRow.font = { bold: true, size: 26 };
  nameRow.height = 40;

  const detailLines = [...company.lines];
  while (detailLines.length < 3) detailLines.push("");
  detailLines.forEach((text) => {
    const row = sheet.addRow([text]);
    sheet.mergeCells(row.number, 1, row.number, colCount);
    row.getCell(1).alignment = { horizontal: "center" };
    row.font = { size: 9, bold: true };
  });

  if (logoId !== null) {
    sheet.addImage(logoId, {
      tl: { col: 0.1, row: 0.1 },
      ext: { width: 130, height: 95 },
    });
  }

  const dateRow = sheet.addRow([dateText]);
  sheet.mergeCells(dateRow.number, 1, dateRow.number, colCount);
  dateRow.getCell(1).alignment = { horizontal: "right" };

  const titleRow = sheet.addRow([titleText]);
  sheet.mergeCells(titleRow.number, 1, titleRow.number, colCount);
  titleRow.getCell(1).alignment = { horizontal: "center" };
  titleRow.getCell(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  titleRow.getCell(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF000000" },
  };
};

// Labels in columns B-D, blank signature line below each
const addSignatures = (sheet) => {
  sheet.addRow([]);
  sheet.addRow([]);
  const labelRow = sheet.addRow(["", ...SIGNATORIES]);
  labelRow.font = { bold: true };
  sheet.addRow([]);
  const lineRow = sheet.addRow(["", "", "", ""]);
  [2, 3, 4].forEach((col) => {
    lineRow.getCell(col).border = { bottom: { style: "thin" } };
  });
};

const saveWorkbook = async (workbook, fileName) => {
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `${fileName}.xlsx`);
};

// Excel workbook, one sheet per day named #1, #2, ...
export const downloadAccountSummaryExcel = async (
  days,
  { title, company, fileName },
) => {
  const workbook = new ExcelJS.Workbook();
  const logoId = addWorkbookLogo(workbook, company);

  days.forEach((day) => {
    const sheet = workbook.addWorksheet(day.label);
    sheet.columns = [
      { width: 8 },
      { width: 32 },
      { width: 28 },
      { width: 40 },
      { width: 16 },
    ];

    addSheetHeader(sheet, {
      company,
      logoId,
      dateText: day.date,
      titleText: `${title} ${day.label}`,
      colCount: 5,
    });

    const columnRow = sheet.addRow(SUMMARY_COLUMNS);
    columnRow.font = { bold: true };
    columnRow.eachCell((cell, col) => {
      cell.alignment = { horizontal: col === 5 ? "right" : "center" };
      cell.border = thinBorder;
    });

    addSummaryRow(sheet, "CASH BALANCE", day.openingBalance);

    day.rows.forEach((item) => {
      const row = sheet.addRow([
        item.ref,
        item.payee,
        item.accountTitle,
        item.particulars,
        item.amount,
      ]);
      row.getCell(1).alignment = { horizontal: "center" };
      // Plain amounts; deposits in green, withdrawals in red
      row.getCell(5).numFmt = AMOUNT_FORMAT;
      row.getCell(5).font = {
        color: { argb: item.type === "Deposit" ? "FF198754" : "FFDC3545" },
      };
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = thinBorder;
      });
    });

    addSummaryRow(sheet, "TOTAL WITHDRAW", day.totalWithdraw);
    addSummaryRow(sheet, "TOTAL DEPOSIT", day.totalDeposit);
    addSummaryRow(sheet, "BALANCE", day.balance, "FFFFC000");
    addSignatures(sheet);
  });

  await saveWorkbook(workbook, fileName);
};

// Each distinct value of `key` (e.g. payee, accountTitle) once, A-Z, with how
// many transactions have it in the range
export const transactionCountsBy = (rows = [], key) => {
  const counts = new Map();
  rows.forEach((row) => {
    const value = row[key];
    if (value) counts.set(value, (counts.get(value) || 0) + 1);
  });
  return [...counts]
    .map(([name, total]) => ({ name, total }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

// Excel workbook with the whole range on one sheet (Summary layout)
export const downloadRangeSummaryExcel = async (
  summary,
  { title, company, period, fileName },
) => {
  const workbook = new ExcelJS.Workbook();
  const logoId = addWorkbookLogo(workbook, company);
  const sheet = workbook.addWorksheet("Summary");
  sheet.columns = [
    { width: 11 },
    { width: 7 },
    { width: 22 },
    { width: 26 },
    { width: 40 },
    { width: 14 },
    { width: 14 },
    { width: 16 },
    { width: 3 },
    { width: 30 },
    { width: 10 },
    { width: 3 },
    { width: 30 },
    { width: 10 },
  ];

  addSheetHeader(sheet, {
    company,
    logoId,
    dateText: period,
    titleText: title,
    colCount: 8,
  });

  // Two small tables to the right, separated by blank spacer columns. Each
  // lists a value once (A-Z) with its transaction count, from the top, not
  // tied to the rows beside it:
  //   NAME | TOTAL           - payees
  //   ACCOUNT TITLE | TOTAL  - account titles
  const SIDE_TABLES = [
    { col: 10, header: "NAME", key: "payee" },
    { col: 13, header: "ACCOUNT TITLE", key: "accountTitle" },
  ];
  const withBorders = (row) =>
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      if (col <= 8) cell.border = thinBorder;
    });

  const columnRow = sheet.addRow(RANGE_COLUMNS);
  columnRow.font = { bold: true };
  columnRow.eachCell((cell, col) => {
    cell.alignment = { horizontal: col >= 6 ? "right" : "center" };
  });
  withBorders(columnRow);
  SIDE_TABLES.forEach(({ col, header }) => {
    const nameCell = columnRow.getCell(col);
    const totalCell = columnRow.getCell(col + 1);
    nameCell.value = header;
    totalCell.value = "TOTAL";
    nameCell.alignment = { horizontal: "center" };
    totalCell.alignment = { horizontal: "right" };
    nameCell.border = thinBorder;
    totalCell.border = thinBorder;
  });

  const cashBalanceRow = sheet.addRow([
    "",
    "",
    "",
    "",
    "CASH BALANCE",
    "",
    "",
    summary.openingBalance,
  ]);
  cashBalanceRow.font = { bold: true };
  cashBalanceRow.getCell(5).alignment = { horizontal: "right" };
  cashBalanceRow.getCell(8).numFmt = AMOUNT_FORMAT;
  withBorders(cashBalanceRow);

  summary.rows.forEach((item) => {
    const row = sheet.addRow([
      item.date,
      item.ref,
      item.payee,
      item.accountTitle,
      item.particulars,
      item.cashIn || null,
      item.cashOut || null,
      item.balance,
    ]);
    row.getCell(2).alignment = { horizontal: "center" };
    [6, 7, 8].forEach((col) => (row.getCell(col).numFmt = AMOUNT_FORMAT));
    // Cash In in green, Cash Out in red
    row.getCell(6).font = { color: { argb: "FF198754" } };
    row.getCell(7).font = { color: { argb: "FFDC3545" } };
    withBorders(row);
  });

  const totalRow = sheet.addRow([
    "",
    "",
    "",
    "",
    "TOTAL",
    summary.totalCashIn,
    summary.totalCashOut,
    summary.balance,
  ]);
  totalRow.font = { bold: true };
  totalRow.getCell(5).alignment = { horizontal: "right" };
  [6, 7, 8].forEach((col) => (totalRow.getCell(col).numFmt = AMOUNT_FORMAT));
  totalRow.getCell(8).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFFFC000" },
  };
  withBorders(totalRow);

  SIDE_TABLES.forEach(({ col, key }) => {
    transactionCountsBy(summary.rows, key).forEach(({ name, total }, index) => {
      const row = sheet.getRow(columnRow.number + 1 + index);
      row.getCell(col).value = name;
      row.getCell(col + 1).value = total;
      row.getCell(col).border = thinBorder;
      row.getCell(col + 1).border = thinBorder;
    });
  });

  addSignatures(sheet);
  await saveWorkbook(workbook, fileName);
};
